import { QueryLoader } from "@Light/components/app/QueryLoader";
import { useAccount } from "@Light/components/page/account";
import { useLight } from "@Light/services/light";
import type {
  LocationPlan,
  LocationRequest,
  RenewalPlanResponse,
} from "@Light/services/lightTypes";
import { useMutation } from "@Light/utils/mutation";
import { RateContext } from "@Light/utils/plan";
import { useEffect, useRef } from "react";
import { useOptionalRenew } from "./flow/context";
import { RenewRateOffer } from "./RenewRateOffer";
import { RenewRoute } from "./routes";

export type RenewRateProps = {
  currentPlan?: LocationPlan;
};

export function RenewRate({ currentPlan: currentPlanProp }: RenewRateProps) {
  const flow = useOptionalRenew();
  const currentPlan = currentPlanProp ?? flow?.currentPlan;
  const account = useAccount();
  const location = account.locations?.[0];
  const { useRequestRenewalPlansMutation } = useLight();
  const requestRates = useMutation<LocationRequest, RenewalPlanResponse[]>(
    useRequestRenewalPlansMutation,
  );
  const didAdvance = useRef(false);

  useEffect(() => {
    if (requestRates.isUninitialized) {
      requestRates.mutate({ uuid: location.uuid });
    }
  }, [requestRates, location?.uuid]);

  useEffect(() => {
    if (!flow?.transitionDate || didAdvance.current) return;
    const confirmRatePath: string = RenewRoute.ConfirmRate;
    if (flow.nav.stack[flow.nav.cursor] !== confirmRatePath) return;
    didAdvance.current = true;
    void flow.next();
  }, [flow, flow?.transitionDate, flow?.nav.cursor]);

  const rate = requestRates.data?.[0];
  const isError = requestRates.isError || (requestRates.isSuccess && !rate);

  if (isError) {
    return (
      <QueryLoader
        queries={[{ isError: true, isLoading: false }]}
        errorMessage="Error querying available rates. Please try again later"
      />
    );
  }

  if (!currentPlan) {
    return null;
  }

  return (
    <RateContext.Provider value={rate}>
      <RenewRateOffer
        currentPlan={currentPlan}
        onBack={flow?.back}
        highlights={rate?.renewal_highlights}
      />
    </RateContext.Provider>
  );
}
